import { useState } from "react";
import { ShieldCheck, X } from "lucide-react";
import { smsApi } from "./api";

/**
 * Termos de uso dos Disparos (SMS e Ligacao) — pedido do dono (28/08/2026):
 * regras, estatisticas e o aviso de que o envio e' TERCEIRIZADO (sem suporte
 * do Nucleo a erro de entrega/cobranca da operadora), tudo com base legal.
 * O texto vive aqui; a versao vigente e' controlada no backend (termos.py).
 */
const SECAO_TERCEIRIZACAO = (servico) => ({
  titulo: "1. O serviço e a terceirização",
  corpo: [
    `${servico} é executado por um gateway de mensageria TERCEIRIZADO e pelas operadoras de telefonia. O NÚCLEO fornece a plataforma — composição de campanhas, base de contatos, métricas e carteira — mas NÃO opera a rede de envio.`,
    "Por isso, falhas de entrega, atrasos, indisponibilidade do serviço de envio, alterações de rota e cobranças praticadas pela operadora fogem ao controle do NÚCLEO e não são de sua responsabilidade, na máxima extensão permitida pela legislação aplicável.",
    "O suporte do NÚCLEO limita-se ao funcionamento da plataforma. Erros de entrega, atrasos de operadora e cobranças de terceiros NÃO possuem suporte, reembolso ou mediação do NÚCLEO.",
  ],
});

const SECAO_LGPD = {
  titulo: "2. Papéis na LGPD — quem responde pelos dados",
  corpo: [
    "Nos termos da Lei Geral de Proteção de Dados (Lei nº 13.709/2018), o ESTABELECIMENTO é o CONTROLADOR dos dados dos seus clientes (art. 5º, VI): decide quem recebe as mensagens e responde pela base legal do contato (art. 7º — consentimento, legítimo interesse ou outra hipótese cabível).",
    "O NÚCLEO atua como OPERADOR (art. 5º, VII), tratando os dados exclusivamente para executar os envios solicitados pelo estabelecimento.",
    "Ao importar números por CSV ou adicioná-los manualmente, o estabelecimento DECLARA possuir base legal para contatar aquelas pessoas. Essa declaração fica registrada com autor, data e hora.",
  ],
};

const SECAO_CREDITOS = (unidade) => ({
  titulo: "5. Créditos e cobrança",
  corpo: [
    `1 crédito = 1 ${unidade}. Recarga mínima de R$ 99,00. Os créditos da carteira NÃO expiram e valem para todos os canais de disparo.`,
    "Estorno automático de créditos: números rejeitados no momento do envio (inválidos, duplicados, descadastrados) e campanhas agendadas canceladas antes do disparo.",
    "NÃO há estorno de envios aceitos pela operadora, sejam eles concluídos ou não (item 4).",
  ],
});

const SECAO_SUSPENSAO = {
  titulo: "6. Suspensão do recurso",
  corpo: [
    "Denúncias de spam, uso sem base legal, tentativa de burlar o descadastro, a janela de horário ou o limite de frequência podem levar à suspensão do recurso de disparos, sem prejuízo das demais funções do sistema e sem direito a indenização.",
  ],
};

const SECAO_VALIDADE = {
  titulo: "7. Validade do aceite e legislação de referência",
  corpo: [
    "O aceite eletrônico destes termos é registrado com usuário, data e hora, e tem validade jurídica na forma do art. 10, § 2º da MP nº 2.200-2/2001 e do art. 441 do Código de Processo Civil (documento eletrônico).",
    "Legislação de referência: Lei nº 13.709/2018 (LGPD) · Lei nº 8.078/1990 (CDC) · Lei nº 12.965/2014 (Marco Civil da Internet) · Resolução Anatel nº 632/2014 · Ato Anatel nº 10.413/2021.",
  ],
};

export const SECOES_SMS = [
  SECAO_TERCEIRIZACAO("O envio de SMS"),
  SECAO_LGPD,
  {
    titulo: "3. Regras de envio por SMS",
    corpo: [
      "Descadastro (opt-out): todo SMS leva a instrução \"Envie PARE p/ sair\". Quem pede sai da base imediatamente e não recebe mais nenhuma campanha (LGPD, art. 18, IX — revogação do consentimento). O bloqueio é automático e definitivo.",
      "Horário: envios só entre 9h e 22h, alinhado às normas de proteção ao consumidor contra contato em horário inconveniente.",
      "Frequência: no máximo 7 mensagens por número a cada 7 dias — proteção contra caracterização de prática abusiva (CDC, art. 39).",
      "Conteúdo: é vedado conteúdo enganoso ou sem identificação do remetente (CDC, arts. 36 e 37), bem como qualquer conteúdo ilícito. O conteúdo é de responsabilidade exclusiva do estabelecimento.",
    ],
  },
  {
    titulo: "4. Estatísticas e limites técnicos do SMS",
    corpo: [
      "A taxa de entrega típica de SMS em campanhas para bases próprias fica entre 85% e 95% — NUNCA 100%. Números inexistentes, aparelhos desligados e bloqueios de operadora são inevitáveis em qualquer base.",
      "A cobrança é POR ENVIO ACEITO pela operadora, entregue ou não — prática padrão do setor de mensageria em todo o mercado.",
      "Os recibos de entrega dependem da operadora: podem levar até 72 horas ou, em parte dos casos, não ser informados.",
      "Mensagens limitadas a 160 caracteres do alfabeto padrão; acentos e emojis não são suportados no envio.",
    ],
  },
  SECAO_CREDITOS("envio de SMS"),
  SECAO_SUSPENSAO,
  SECAO_VALIDADE,
];

export const SECOES_VOZ = [
  SECAO_TERCEIRIZACAO("O disparo de ligações com áudio (torpedo de voz)"),
  SECAO_LGPD,
  {
    titulo: "3. Regras das ligações",
    corpo: [
      "Áudio entre 8 e 29 segundos — a operadora ENCERRA toda ligação aos 30 segundos, sem exceção.",
      "Horário: ligações só entre 9h e 22h. Fora desse intervalo, a operadora pode reter a chamada em fila sem aviso até o horário comercial seguinte.",
      "Frequência: no máximo 7 contatos por número a cada 7 dias — proteção contra caracterização de prática abusiva (CDC, art. 39).",
      "Descadastro: a ligação não tem resposta \"PARE\", mas todo pedido de descadastro recebido por qualquer canal (SMS, atendimento, WhatsApp) vale também para as ligações — o bloqueio da base é único.",
      "Telemarketing ativo por voz sujeita-se às regras da Anatel — incluindo a identificação de chamadas com o prefixo 0303 (Ato nº 10.413/2021), aplicada pela operadora quando cabível — e aos cadastros públicos de bloqueio (naomeperturbe.com.br).",
      "Conteúdo do áudio: lícito e com identificação do estabelecimento (CDC, arts. 36 e 37); de responsabilidade exclusiva do estabelecimento.",
    ],
  },
  {
    titulo: "4. Estatísticas e limites técnicos das ligações",
    corpo: [
      "O dado confiável reportado pela operadora é \"discada\". A informação de ATENDIMENTO nem sempre é reportada — a taxa de atendimento exibida pode ficar incompleta por limitação da operadora, não do NÚCLEO.",
      "A cobrança é POR LIGAÇÃO ACEITA pela operadora, atendida ou não — prática padrão do setor.",
      "Ligações para números ocupados ou não atendidos podem ser RETENTADAS automaticamente pela operadora, sem novo custo e sem aviso.",
      "Chamadas são encerradas aos 30 segundos (por isso o teto de 29s no áudio).",
    ],
  },
  SECAO_CREDITOS("ligação"),
  SECAO_SUSPENSAO,
  SECAO_VALIDADE,
];


export default function TermosDisparos({ canal = "sms", somenteLeitura, onAceito, onFechar }) {
  const ehVoz = canal === "voz";
  const secoes = ehVoz ? SECOES_VOZ : SECOES_SMS;
  const [li, setLi] = useState(false);
  const [enviando, setEnviando] = useState(false);

  const aceitar = async () => {
    setEnviando(true);
    try {
      const r = await smsApi.aceitarTermos(canal);
      onAceito?.(r);
    } catch {
      window.alert("Não foi possível registrar o aceite — tente de novo.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm grid place-items-center p-4">
      <div className="w-full max-w-2xl max-h-[88vh] rounded-2xl border bg-card shadow-2xl flex flex-col">
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b shrink-0">
          <h2 className="font-semibold flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-primary" />
            {ehVoz ? "Termos de Uso — Disparos por Ligação" : "Termos de Uso — Disparos por SMS"}
          </h2>
          {/* O X aparece também antes do aceite: sem ele, quem abria "Ler e aceitar
              os termos" só saía aceitando ou recarregando a página (25/10/2026). */}
          {onFechar && (
            <button onClick={onFechar} aria-label="Fechar" title="Fechar" className="rounded-lg p-1.5 hover:bg-muted transition-colors">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 text-sm">
          <p className="text-xs text-muted-foreground">Versão 1.0 · 28/08/2026</p>
          {secoes.map((s) => (
            <section key={s.titulo}>
              <h3 className="font-semibold mb-1.5">{s.titulo}</h3>
              <div className="space-y-1.5 text-muted-foreground">
                {s.corpo.map((par, i) => <p key={i}>{par}</p>)}
              </div>
            </section>
          ))}
        </div>

        <div className="px-5 py-4 border-t shrink-0">
          {somenteLeitura ? (
            <button onClick={onFechar}
              className="w-full rounded-lg border px-4 py-2.5 text-sm font-medium hover:border-primary transition-colors">
              Fechar
            </button>
          ) : (
            <>
              <label className="flex items-start gap-2.5 cursor-pointer text-sm mb-3">
                <input type="checkbox" checked={li} onChange={(e) => setLi(e.target.checked)}
                  className="rounded border-input mt-0.5" />
                <span>
                  Li e concordo com os termos acima, inclusive com a <strong>terceirização
                  {ehVoz ? " das ligações" : " do envio"}</strong> e
                  os <strong>limites de responsabilidade e suporte</strong> do NÚCLEO.
                </span>
              </label>
              <button onClick={aceitar} disabled={!li || enviando}
                className="w-full rounded-lg bg-primary text-primary-foreground px-4 py-2.5 font-semibold disabled:opacity-40">
                {enviando ? "Registrando..." : ehVoz ? "Concordar e liberar as ligações" : "Concordar e liberar o SMS"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
